/**
 * DocumentViewer Component
 * Displays document content with search result highlighting
 */

import React from 'react';
import { FiFileText, FiTarget } from 'react-icons/fi';
import type { KnowledgeDocument, KnowledgeSearchResult } from '../../types';

interface DocumentViewerProps {
  document: KnowledgeDocument | null;
  searchResults?: KnowledgeSearchResult[];
  className?: string;
}

export const DocumentViewer: React.FC<DocumentViewerProps> = ({
  document,
  searchResults = [],
  className = ''
}) => {
  if (!document) {
    return (
      <div className={`h-full bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 flex items-center justify-center ${className}`}>
        <div className="text-center p-8">
          <FiFileText className="mx-auto text-gray-500 mb-4" size={64} />
          <h3 className="text-xl font-semibold text-gray-400 mb-2">No document selected</h3>
          <p className="text-sm text-gray-500">
            Select a document from the list to view its content
          </p>
        </div>
      </div>
    );
  }

  // Find search results for this document
  const relevantResults = searchResults.filter(r => r.document && r.document.id === document.id);

  return (
    <div className={`h-full bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 flex flex-col ${className}`}>
      {/* Header */}
      <div className="p-4 border-b border-slate-700">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-white truncate">
              {document.title || document.filename}
            </h3>
            <div className="flex items-center gap-3 text-sm text-gray-400 mt-1">
              <span>{document.mimeType}</span>
              <span>•</span>
              <span>{document.uploadedAt && new Date(document.uploadedAt).toLocaleString()}</span>
            </div>
          </div>

          {document.status && (
            <span className={`
              px-3 py-1 text-sm rounded-lg
              ${document.status === 'completed' ? 'bg-green-500/20 text-green-400' : ''}
              ${document.status === 'processing' ? 'bg-yellow-500/20 text-yellow-400' : ''}
              ${document.status === 'failed' ? 'bg-red-500/20 text-red-400' : ''}
            `}>
              {document.status}
            </span>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Metadata */}
        {document.metadata && Object.keys(document.metadata).length > 0 && (
          <div className="p-4 border-b border-slate-700 bg-slate-900/50">
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Metadata</h4>
            <div className="grid grid-cols-2 gap-2 text-xs">
              {Object.entries(document.metadata).map(([key, value]) => (
                <div key={key}>
                  <span className="text-gray-500">{key}:</span>{' '}
                  <span className="text-gray-300">{String(value)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Search Results (if any) */}
        {relevantResults.length > 0 && (
          <div className="p-4 border-b border-slate-700 bg-blue-500/5">
            <div className="flex items-center gap-2 mb-3">
              <FiTarget className="text-blue-400" size={16} />
              <h4 className="text-sm font-semibold text-blue-300">
                {relevantResults.length} Relevant Sections
              </h4>
            </div>
            <div className="space-y-3">
              {relevantResults.map((result, idx) => (
                <div key={idx} className="bg-slate-800/50 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-gray-400">
                      Score: {(result.score * 100).toFixed(1)}%
                    </span>
                    {result.pageNumber && (
                      <span className="text-xs text-gray-400">
                        Page {result.pageNumber}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-300 leading-relaxed">
                    {result.content}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Full Content */}
        {document.content && (
          <div className="p-4">
            <h4 className="text-sm font-semibold text-gray-300 mb-3">Full Content</h4>
            <div className="prose prose-invert prose-sm max-w-none">
              <pre className="whitespace-pre-wrap text-gray-300 leading-relaxed font-sans">
                {document.content}
              </pre>
            </div>
          </div>
        )}

        {/* No content available */}
        {!document.content && relevantResults.length === 0 && (
          <div className="p-8 text-center">
            <p className="text-gray-400">No content preview available</p>
            {document.status === 'processing' && (
              <p className="text-sm text-gray-500 mt-2">
                Document is still being processed
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentViewer;
